import React, { useEffect, useRef, useState } from 'react';
import { Uploader, Button as RsButton } from 'rsuite';
import { FileType } from 'rsuite/esm/Uploader';
import styled from 'styled-components';
import useIsMobile from 'hooks/useIsMobile';
import { COLOR_PRIMARY } from 'constants/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import refreshTokenId from 'utils/firebase.util';
import {
  getDevImpersonateUserId,
  isDevUserSwitchEnabled,
} from 'utils/devUserSwitch';
import { useFormLocked } from 'components/ui/FormLock';
import styles from './FileUploader.module.scss';

const DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const FILE_INPUT_ID = 'scla-file-input';

export interface IListedFile extends FileType {
  size?: number | string;
  mimeType?: string;
}

function isAllowedSclaDocument(file: FileType): boolean {
  const name = (file.blobFile?.name || file.name || '').toLowerCase();
  const mime = (file.blobFile?.type || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  if (ext === '.pdf' && mime === 'application/pdf') return true;
  if ((ext === '.jpg' || ext === '.jpeg') && mime === 'image/jpeg') return true;
  if (ext === '.png' && mime === 'image/png') return true;
  return false;
}

function fileKind(file: IListedFile): 'pdf' | 'image' | 'other' {
  const name = (file.name || '').toLowerCase();
  const mime = (file.mimeType || file.blobFile?.type || '').toLowerCase();
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (mime === 'image/jpeg' || mime === 'image/png') return 'image';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')) return 'image';
  return 'other';
}

function formatFileSize(value: number | string | undefined): string {
  if (value === undefined || value === '') return '';
  const bytes = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  const shown = mb >= 10 ? String(Math.round(mb)) : mb.toFixed(1).replace(/\.0$/, '');
  return `${shown} MB`;
}

function fileSummary(file: IListedFile): string {
  const name = file.name || 'File';
  const size = formatFileSize(file.size ?? file.blobFile?.size);
  if (!size) return name;
  return `${name} · ${size}`;
}

function ImageThumb({ file }: { file: IListedFile }) {
  const [src, setSrc] = useState(file.url || '');

  useEffect(() => {
    if (file.url) {
      setSrc(file.url);
      return undefined;
    }
    if (!file.blobFile) return undefined;
    const next = URL.createObjectURL(file.blobFile);
    setSrc(next);
    return () => URL.revokeObjectURL(next);
  }, [file.url, file.blobFile]);

  if (!src) return <span className={styles.pdfMark} aria-hidden="true">IMG</span>;
  return <img className={styles.thumb} src={src} alt="" />;
}

function FileRows({
  files,
  onRemove,
}: {
  files: IListedFile[];
  onRemove?: (file: FileType) => void;
}) {
  if (!files.length) return null;
  return (
    <ul className={styles.fileList}>
      {files.map(file => {
        const kind = fileKind(file);
        const name = file.name || 'File';
        return (
          <li className={styles.fileRow} key={file.fileKey || name}>
            {kind === 'image' ? (
              <ImageThumb file={file} />
            ) : (
              <span className={styles.pdfMark} aria-hidden="true">
                {kind === 'pdf' ? 'PDF' : 'FILE'}
              </span>
            )}
            <span className={styles.fileName}>{fileSummary(file)}</span>
            <span className={styles.fileActions}>
              {file.url ? (
                <a className={styles.viewLink} href={file.url} target="_blank" rel="noreferrer">
                  View
                </a>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  className={styles.removeFile}
                  aria-label={`Remove file: ${name}`}
                  onClick={() => onRemove(file)}
                >
                  Remove
                </button>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const ContainerDragger = styled.div`
  height: 200px;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
`;

const FileDescription = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileContainer = styled.div`
  height: 200px;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px 25px;

  button {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${COLOR_PRIMARY};
    color: #fff;
    font-size: 21px;
  }
`;

interface IFileUploaderProps {
  isLoading: boolean;
  onError: any;
  onSuccess: any;
  onRemove: any;
  files: IListedFile[];
  url: string;
  setIsLoading: (b: boolean) => any;
  readOnly?: boolean;
  inputLabel?: string;
  hint?: string;
  documentsOnly?: boolean;
}
export default function FileUploader(props: IFileUploaderProps) {
  const {
    setIsLoading,
    url,
    files,
    isLoading,
    onError,
    onRemove,
    onSuccess,
    readOnly = false,
    inputLabel,
    hint,
    documentsOnly = false,
  } = props;

  const locked = useFormLocked();
  const inactive = readOnly || locked;
  const { isMobile } = useIsMobile();
  const authHeaders = useRef<Record<string, string>>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const [typeError, setTypeError] = useState('');

  useEffect(() => {
    if (!documentsOnly) return undefined;
    const input = rootRef.current?.querySelector('input[type="file"]');
    if (!(input instanceof HTMLInputElement)) return undefined;
    input.id = FILE_INPUT_ID;
    if (inputLabel) input.setAttribute('aria-label', inputLabel);
    input.accept = DOCUMENT_ACCEPT;
    return undefined;
  }, [documentsOnly, inputLabel, files.length]);

  // rsuite skips its own "not uploaded yet" check when shouldUpload returns
  // a promise, so check the file status here or earlier files are re-sent.
  const shouldUpload = async (file: FileType) => {
    if (inactive) return false;
    if (file.status && file.status !== 'inited') return false;
    if (documentsOnly && !isAllowedSclaDocument(file)) {
      setTypeError('Use a PDF, JPG or PNG file.');
      return false;
    }

    const token = await refreshTokenId();
    if (token) {
      authHeaders.current.Authorization = `Bearer ${token}`;
    } else {
      delete authHeaders.current.Authorization;
    }

    if (isDevUserSwitchEnabled()) {
      const impersonateUserId = getDevImpersonateUserId();
      if (impersonateUserId) {
        authHeaders.current['X-Test-User-Id'] = impersonateUserId;
      } else {
        delete authHeaders.current['X-Test-User-Id'];
      }
    }

    return true;
  };

  const onSuccessHandler = (
    response: any,
    file: FileType,
    event: ProgressEvent<EventTarget>,
  ) => {
    const element = document.querySelectorAll(`.file-upload`);

    if (element && element[0]) {
      const i = element.length - 1;
      // scroll to error input
      element[i].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    onSuccess(response, file, event);
  };

  const queueDocument = (_list: FileType[], incoming: FileType[] | FileType) => {
    const next = Array.isArray(incoming) ? incoming : [incoming];
    if (next.some(file => !isAllowedSclaDocument(file))) {
      setTypeError('Use a PDF, JPG or PNG file.');
      return false;
    }
    setTypeError('');
    return true;
  };

  if (documentsOnly) {
    return (
      <div ref={rootRef} className={inactive ? styles.uploadLocked : undefined}>
        {inputLabel ? (
          <label className={styles.uploadLabel} htmlFor={FILE_INPUT_ID}>
            {inputLabel}
          </label>
        ) : null}
        <Uploader
          disabled={isLoading || inactive}
          onError={onError}
          onSuccess={onSuccessHandler}
          onRemove={onRemove}
          draggable
          accept={DOCUMENT_ACCEPT}
          fileListVisible={false}
          fileList={files}
          action={url}
          headers={authHeaders.current}
          shouldUpload={shouldUpload}
          shouldQueueUpdate={queueDocument}
          multiple={false}
        >
          <ContainerDragger>
            <p>
              <b>Click or drag a file here</b>
            </p>
            <RsButton>
              {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Select files...'}
            </RsButton>
          </ContainerDragger>
        </Uploader>
        {hint ? <p className={styles.hint}>{hint}</p> : null}
        {typeError ? (
          <p className={styles.typeError} role="alert">
            {typeError}
          </p>
        ) : null}
        <FileRows files={files} onRemove={inactive ? undefined : onRemove} />
      </div>
    );
  }

  if (inactive) {
    if (!files.length) return <p>No files uploaded.</p>;
    return (
      <ul>
        {files.map(file => (
          <li key={file.fileKey || file.name}>
            <a href={file.url} target="_blank" rel="noreferrer">
              {file.name}
            </a>
          </li>
        ))}
      </ul>
    );
  }

  if (isMobile) {
    return (
      <Uploader
        disabled={isLoading}
        onError={onError}
        multiple={false}
        onProgress={() => {
          setIsLoading(true);
        }}
        onSuccess={onSuccessHandler}
        onRemove={onRemove}
        draggable
        listType="picture-text"
        fileList={files}
        action={url}
        headers={authHeaders.current}
        shouldUpload={shouldUpload}
        renderFileInfo={(file: FileType) => (
          <FileDescription className="file-upload">
            <a href={file.url} target="_blank" rel="noreferrer">
              {file.name}
            </a>
          </FileDescription>
        )}
      >
        <MobileContainer>
          <RsButton>
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              'Tap here to upload a file'
            )}
          </RsButton>
        </MobileContainer>
      </Uploader>
    );
  }

  return (
    <Uploader
      disabled={isLoading}
      onError={onError}
      onSuccess={onSuccess}
      onRemove={onRemove}
      draggable
      listType="picture-text"
      fileList={files}
      action={url}
      headers={authHeaders.current}
      shouldUpload={shouldUpload}
      multiple={false}
      renderFileInfo={(file: FileType) => (
        <FileDescription className="file-upload">
          <a href={file.url} target="_blank" rel="noreferrer">
            {file.name}
          </a>
        </FileDescription>
      )}
    >
      <ContainerDragger>
        <p>
          <b>Click or Drag files to this area to upload</b>
        </p>
        <RsButton>Select files...</RsButton>
      </ContainerDragger>
    </Uploader>
  );
}
